/**
 * A zip archive built in the browser, STORED (no compression), from bytes already fetched.
 *
 * WHY HERE AND NOT IN A FUNCTION. "Downloads never come through Lambda" (Ed, 2026-09-30): a
 * bundle built server-side would either stream the archive back through a 6 MB response cap or
 * write it to S3 and mint a URL, moving every file through a function to reach a place the browser
 * can already fetch from. The files are already reachable one presigned URL each; the archive is
 * eighty lines of bookkeeping around them, so it is done where the bytes already are.
 *
 * STORED, NOT DEFLATED. The files this bundles are PDFs, images and office documents, every one of
 * them already compressed; deflating them again saves a few percent and costs a dependency. A
 * STORED entry is a header, the bytes, and a CRC, which is what this writes.
 *
 * ZIP32 ONLY: no entry over 4 GB and no more than 65,535 entries, both far beyond what a record
 * carries; refused rather than silently wrong.
 */

const TABLE = (() => {
    const out = new Uint32Array(256);
    for (let n = 0; n < 256; n += 1) {
        let c = n;
        for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xEDB88320 ^ (c >>> 1) : c >>> 1;
        out[n] = c >>> 0;
    }
    return out;
})();

export function crc32(bytes: Uint8Array): number {
    let crc = 0xFFFFFFFF;
    for (let i = 0; i < bytes.length; i += 1) crc = TABLE[(crc ^ bytes[i]) & 0xFF] ^ (crc >>> 8);
    return (crc ^ 0xFFFFFFFF) >>> 0;
}

export type ZipEntry = {name: string; bytes: Uint8Array; modified?: Date};

function dosDateTime(when: Date): {date: number; time: number} {
    const year = Math.max(1980, when.getFullYear());
    return {
        date: ((year - 1980) << 9) | ((when.getMonth() + 1) << 5) | when.getDate(),
        time: (when.getHours() << 11) | (when.getMinutes() << 5) | Math.floor(when.getSeconds() / 2),
    };
}

/** A name the archive and every extractor accept: forward slashes, no leading slash, no control characters. */
function entryName(name: string, taken: Set<string>): string {
    let base = name.replace(/\\/g, "/").replace(/^\/+/, "").replace(/[\u0000-\u001f]/g, "").trim() || "file";
    let candidate = base;
    for (let n = 2; taken.has(candidate.toLowerCase()); n += 1) {
        const dot = base.lastIndexOf(".");
        candidate = dot > 0 ? `${base.slice(0, dot)} (${n})${base.slice(dot)}` : `${base} (${n})`;
    }
    taken.add(candidate.toLowerCase());
    return candidate;
}

export function buildZipBytes(entries: ZipEntry[]): Uint8Array {
    if (entries.length > 0xFFFF) throw new Error("Too many files for one archive.");
    const encoder = new TextEncoder();
    const parts: Uint8Array[] = [];
    const central: Uint8Array[] = [];
    const taken = new Set<string>();
    let offset = 0;
    for (const entry of entries) {
        if (entry.bytes.length >= 0xFFFFFFFF) throw new Error(`${entry.name} is too large for one archive.`);
        const name = encoder.encode(entryName(entry.name, taken));
        const crc = crc32(entry.bytes);
        const {date, time} = dosDateTime(entry.modified ?? new Date());
        const local = new DataView(new ArrayBuffer(30));
        local.setUint32(0, 0x04034b50, true);   // local file header
        local.setUint16(4, 20, true);           // version needed: 2.0
        local.setUint16(6, 0x0800, true);       // flags: names are UTF-8
        local.setUint16(8, 0, true);            // method: STORED
        local.setUint16(10, time, true);
        local.setUint16(12, date, true);
        local.setUint32(14, crc, true);
        local.setUint32(18, entry.bytes.length, true);
        local.setUint32(22, entry.bytes.length, true);
        local.setUint16(26, name.length, true);
        local.setUint16(28, 0, true);
        const header = new DataView(new ArrayBuffer(46));
        header.setUint32(0, 0x02014b50, true);  // central directory header
        header.setUint16(4, 20, true);
        header.setUint16(6, 20, true);
        header.setUint16(8, 0x0800, true);
        header.setUint16(10, 0, true);
        header.setUint16(12, time, true);
        header.setUint16(14, date, true);
        header.setUint32(16, crc, true);
        header.setUint32(20, entry.bytes.length, true);
        header.setUint32(24, entry.bytes.length, true);
        header.setUint16(28, name.length, true);
        header.setUint16(30, 0, true);          // extra
        header.setUint16(32, 0, true);          // comment
        header.setUint16(34, 0, true);          // disk
        header.setUint16(36, 0, true);          // internal attributes
        header.setUint32(38, 0, true);          // external attributes
        header.setUint32(42, offset, true);     // where the local header sits
        parts.push(new Uint8Array(local.buffer), name, entry.bytes);
        central.push(new Uint8Array(header.buffer), name);
        offset += 30 + name.length + entry.bytes.length;
    }
    const centralSize = central.reduce((sum, one) => sum + one.length, 0);
    const end = new DataView(new ArrayBuffer(22));
    end.setUint32(0, 0x06054b50, true);         // end of central directory
    end.setUint16(4, 0, true);
    end.setUint16(6, 0, true);
    end.setUint16(8, entries.length, true);
    end.setUint16(10, entries.length, true);
    end.setUint32(12, centralSize, true);
    end.setUint32(16, offset, true);
    end.setUint16(20, 0, true);
    const pieces = [...parts, ...central, new Uint8Array(end.buffer)];
    const out = new Uint8Array(pieces.reduce((sum, one) => sum + one.length, 0));
    let at = 0;
    for (const piece of pieces) { out.set(piece, at); at += piece.length; }
    return out;
}

export function buildZip(entries: ZipEntry[]): Blob {
    return new Blob([buildZipBytes(entries)], {type: "application/zip"});
}

/** Hand the archive to the browser as a file. Same origin, so `download` names it. */
export function saveBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}
